import React, { useEffect, useRef, useState } from 'react';
import { WearerSummary } from 'src/components/BundleProduct/BundleOptionsProvider';
import { Subject } from 'rxjs';

import { Svg } from '../Svg/Svg';
import { FormData } from '../BundleProduct/utils/formDataToPayload';

import classes from './WearerBundleSummary.scss';

export type Props = {
    summary: WearerSummary[];
    qtyMap: Record<number, number>;
    current: number;
    parentId: number;
    formData$: Subject<FormData>;
};

export function WearerBundleSummary(props: Props) {
    return (
        <div className={classes.summary}>
            <h2 className={classes.summaryTitle}>
                Create your bundle{' '}
                <span className={classes.step}>
                    (step {props.current + 1} of {props.summary.length + 1})
                </span>
            </h2>
            <div className={classes.summaryContent}>
                {props.summary && (
                    <ul className={classes.summaryList}>
                        {props.summary.map((item, index) => {
                            const currQty = props.qtyMap[item.id] ?? 0;
                            return (
                                <SummaryItem
                                    key={item.id}
                                    active={props.current === index}
                                    item={item}
                                    currQty={currQty}
                                    formData$={props.formData$}
                                />
                            );
                        })}
                        <li
                            className={classes.summaryItem}
                            key="add-logo"
                            style={{ order: props.summary.length }}
                            data-active={String(props.current === props.summary.length)}
                        >
                            <img
                                className={classes.summaryImage}
                                src={require(`../../svg/free-logo.svg`)}
                                alt="Free logo"
                            />
                            <span className={classes.summaryName}>Add your logo</span>
                        </li>
                    </ul>
                )}
            </div>
        </div>
    );
}

type ItemProps = {
    item: WearerSummary;
    currQty: number;
    active: boolean;
    formData$: Subject<FormData>;
};

const SummaryItem = React.memo((props: ItemProps) => {
    const { currQty, active, item } = props;
    const { name, image, maxQty, id, position } = item;

    /**
     * Keep track of the current QTY for this line item
     */
    const ref = useRef<number>(currQty);
    const [liveValue, setLiveQty] = useState(currQty);

    /**
     * If this item is active, use the 'live' value which is
     * coming from the form stream
     */
    const qtyValue = active ? liveValue : currQty;

    /**
     * Subscribe to form changes that may be related to this
     * line item. Note: we subscribe here for live-updates,
     * but when this line-item is no longer 'active' then
     * we defer back to the value in the stored state
     */
    useEffect(() => {
        /**
         * if this item is no-longer active, reset it's value to that
         * stored in the global state
         */
        if (!props.active) {
            setLiveQty(ref.current);
            return;
        }
        /**
         * Otherwise subscribe to 'live' changes here
         */
        const sub = props.formData$.subscribe((x) => {
            if (x.parentId === props.item.id) {
                setLiveQty(x.totalQty);
            }
        });
        return () => {
            sub.unsubscribe();
        };
    }, [props.active, props.formData$, props.item.id]);

    let status: 'low' | 'high' | 'complete' | 'idle' = 'idle';
    if (qtyValue > maxQty) {
        status = 'high';
    }
    if (qtyValue > 1 && qtyValue < maxQty) {
        status = 'low';
    }
    if (qtyValue === maxQty) {
        status = 'complete';
    }

    return (
        <li
            className={classes.summaryItem}
            key={id}
            style={{ order: position }}
            data-active={String(active)}
            data-status={status}
        >
            <img className={classes.summaryImage} src={image} alt={name} />
            <span className={classes.summaryName}>{name}</span>
            <div className={classes.status}>
                {status === 'complete' && (
                    <span className={classes.icon}>
                        <Svg name="check" />
                    </span>
                )}
                <span className={classes.summaryCounter}>
                    {qtyValue} / {maxQty}
                </span>
            </div>
        </li>
    );
});
